import { FC, Fragment, useCallback, useEffect, useState } from "react";
import CommentListing from "components/CommentListing/CommentListing";
import StartRating from "components/StartRating/StartRating";
import Avatar from "shared/Avatar/Avatar";
import Badge from "shared/Badge/Badge";
import { useNavigate, useParams, Link } from "react-router-dom";
import { AMENITIES_ICONS } from "../Common/constant";
import { Dialog, Transition } from "@headlessui/react";
import { XCircleIcon } from "@heroicons/react/24/outline";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import ButtonClose from "shared/ButtonClose/ButtonClose";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import DetailPagetLayout from "../Common/Layout";
import useWindowSize from "application/hooks/useWindowResize";
import { useTurnstile } from "application/hooks/useTurnstile";
import "react-responsive-carousel/lib/styles/carousel.min.css"; // requires a loader
import { Carousel } from "react-responsive-carousel";
import {
  useGetHolidayPackageQuery,
  useGetHolidayPackageStatsQuery,
  useLazyGetHolidayPackageAvailabilityByDatesQuery,
  useLazyGetHolidayPackageAvailabilityByRangeQuery,
  useSendPackageRequestMutation,
} from "application/features/holidayPackage/holidayPackageApiSlice";
import Skeleton from "react-loading-skeleton";
import MobileFooterSticky from "../Common/MobileFooterSticky";
import LocationsMap from "components/GoogleMaps/LocationsMap";
import moment from "moment";
import {
  isWithinLatestBooking,
  buildPackageRequestPayload,
} from "application/utils/latestBooking";
import SectionGrid from "components/ComponentsToMove/SectionGrid";
import { BookingDataType, GuestsObject } from "data/interfaces";
import InputBar from "components/InputBar/InputBar";
import { useDispatch } from "react-redux";
import {
  selectRange,
  setBooking,
} from "application/features/bookings/bookingsSlice";
import { selectCheckIn, selectCheckOut } from "application/features/search/searchSlice";
import { useLazyGetExperiencesNearbyQuery } from "application/features/experience/experienceApiSlice";
import { useSelector } from "react-redux";
import {
  selectExperiencesList,
  setExperiencesList,
} from "application/features/experience/experienceSlice";
import ModalLogin from "../Common/ModalLogin";
import { selectCurrentToken, selectCurrentLoggedClient } from "application/features/clientAuth/authClientSlice";
import {
  selectGuests,
  selectGuestsCount,
} from "application/features/guests/guestsSlice";
import {
  setAvailability,
} from "application/features/property/propertySlice";
import { Bar, Doughnut } from "react-chartjs-2";
import "chart.js/auto";
import { toast } from "react-toastify";
import { MoodEnum, StockEnum, StockNumericType } from "data/enums";
import { stockDetailPath } from "data/stock_types";
import { useLazyGetReviewByPropertyUUIDQuery } from "application/features/review/reviewApiSlice";
import Pagination from "shared/Pagination/Pagination";
import LikeSaveBtns from "components/LikeSaveBtns";
import { useTranslation } from "react-i18next";
import StayDatesRangeInputWrapper from "../Common/StayDatesRangeInputWrapper";
import GuestsInput from "../Common/GuestsInput";
import {
  formatPrice,
} from "shared/AppFunctions/formatPrice";
import returnMoodColor from "shared/AppFunctions/returnMoodColor";
import FullImageCard from "components/Quasi/FullImageCard";
import ImageMosaicGallery from "components/Quasi/ImageMosaicGallery/ImageMosaicGallery";
import AmenitiesTag from "components/Quasi/AmenitiesTag/AmenitiesTag";
import SectionStayDays from "components/Quasi/SectionStayDays";

const HolidayPackagePageContainer: FC<{}> = () => {
  // STATES
  const searchCheckIn = useSelector(selectCheckIn);
  const searchCheckOut = useSelector(selectCheckOut);
  const [startDate, setStartDate] = useState<Date>(
    searchCheckIn ? moment(searchCheckIn).toDate() : moment().add(1, "day").toDate(),
  );
  const [endDate, setEndDate] = useState<Date>(
    searchCheckOut ? moment(searchCheckOut).toDate() : moment().add(3, "day").toDate(),
  );
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
  const [showFooterSticky, setShowFooterSticky] = useState(true);
  const [radius, setRadius] = useState<number>(25);
  const [totalFormattedPrice, setTotalFormattedPrice] = useState<string>("0");

  let [isOpenModalAmenities, setIsOpenModalAmenities] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loading, setLoading] = useState(false);

  let [isImageFullScreen, setIsImageFullScreen] = useState("");

  // PARAMS
  const id = useParams().id;

  // HOOKS
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { width, height } = useWindowSize();

  // QUERIES
  const { data: listing, isLoading, isSuccess, isError } = useGetHolidayPackageQuery(id);
  // `id` may be a slug or a uuid; secondary uuid-based queries use the
  // canonical uuid from the loaded listing.
  const packageUuid = String(listing?.holidayPackage?.package_uuid ?? "");
  const [fetchReviews, { data: reviews, isLoading: isLoadingReviews, isSuccess: isSuccessReviews }] = useLazyGetReviewByPropertyUUIDQuery();
  const [fetchExperiencesNearby, { data: experiencesNearby, isLoading: isLoadingExperiences, isSuccess: isSuccessExperiencesNearby }] = useLazyGetExperiencesNearbyQuery({});
  const { data: propertyStats, isLoading: isLoadingStats } = useGetHolidayPackageStatsQuery(packageUuid, { skip: !packageUuid });
  const [checkIfPropertyIsAvailable, { data: propertyIsAvailable, isLoading: isCheckingPropertyAvailability }] = useLazyGetHolidayPackageAvailabilityByDatesQuery({});
  const [checkAvailabilityInRange, { data: rangeAvailability, isLoading: isLoadingRangeAvailability }] = useLazyGetHolidayPackageAvailabilityByRangeQuery({});
  const [sendPackageRequest, { isLoading: isSendingRequest }] = useSendPackageRequestMutation();
  const { getToken: getCaptchaToken } = useTurnstile("package_request");

  // Handle request booking (when within latest_booking constraint)
  const handleRequestBooking = async () => {
    if (!client_token) {
      setShowLoginModal(true);
      return;
    }

    const totalGuests = (guests.adults || 0) + (guests.children || 0) + (guests.infants || 0);

    let captchaToken: string | null;
    try {
      captchaToken = await getCaptchaToken();
    } catch (error) {
      console.error("Turnstile verification failed:", error);
      toast.error(t("translation:captcha.error"));
      return;
    }

    try {
      const requestData = buildPackageRequestPayload({
        packageName: listing.holidayPackage?.name ?? "",
        packageUuid: listing.holidayPackage?.package_uuid ?? "",
        startDate: startDate ?? "",
        endDate: endDate ?? "",
        guests: totalGuests,
        price: totalFormattedPrice,
        hostName: listing?.host?.name,
        clientEmail: loggedClient?.email ?? "",
      });

      const response = await sendPackageRequest({
        ...requestData,
        captcha_token: captchaToken,
      }).unwrap();
      toast.success(t("translation:detailPages.common.requestSent"));
    } catch (error) {
      toast.error(t("translation:detailPages.common.requestError"));
      console.error("Error sending package request:", error);
    }
  };

  // SELECTORS
  const experiencesList = useSelector(selectExperiencesList);
  const client_token = useSelector(selectCurrentToken);
  const loggedClient = useSelector(selectCurrentLoggedClient);
  const range = useSelector(selectRange);
  const guests: GuestsObject = useSelector(selectGuests);
  const guestCount: number = useSelector(selectGuestsCount);

  // FETCH FUNCTIONS
  const handleFetchExperiences = async () => {
    const latitude = listing.holidayPackage?.location?.latitude;
    const longitude = listing.holidayPackage?.location?.longitude;
    if (latitude == null || longitude == null) {
      // No geographic anchor on this package; skip the nearby lookup.
      return;
    }
    try {
      const res = await fetchExperiencesNearby({
        latitude,
        longitude,
        radius: radius,
      }).unwrap();

      // Add href field to each experience for navigation
      const experiencesWithHref = res.map((experience: any) => ({
        ...experience,
        href: stockDetailPath(StockNumericType.EXPERIENCE, experience.slug || experience.experience_uuid)
      }));

      dispatch(setExperiencesList(experiencesWithHref));
    } catch (error) {
      console.log(error);
      // navigate("/500");
    }
  };

  const handleOpenModalImageGallery = () => {
    // router(`${thisPathname}/?modal=PHOTO_TOUR_SCROLLABLE`);
  };

  // TRANSLATIONS
  const { t, ready } = useTranslation("translation");

  useEffect(() => {
    if (packageUuid) fetchReviews({ property_uuid: packageUuid, page: 1 });
  }, [packageUuid]);

  useEffect(() => {
    // if listing is undefined return then navigate to not found page
    if (isError && !isLoading) {
      navigate("/404");
    }
  }, [isError, isLoading]);

  useEffect(() => {
    if (listing?.holidayPackage?.in_review) {
      navigate("/in-review", {
        state: { type: "holiday-package", name: listing.holidayPackage.name },
        replace: true,
      });
    }
  }, [listing]);

  useEffect(() => {
    const price = formatPrice(
      listing?.holidayPackage?.price * (guestCount || 1) || 0,
      true,
      window.navigator.language,
      listing?.holidayPackage?.currency || "EUR"
    );
    setTotalFormattedPrice(price);
  }, [
    guestCount,
    listing?.holidayPackage?.price,
    listing?.holidayPackage?.currency,
  ]);

  const handleStartBooking = async (skipClientControl: boolean) => {
    console.log(listing);
    // set booking data to redux
    if (!client_token && !skipClientControl) {
      setShowLoginModal(true);
    } else {
      setShowLoginModal(false);
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
      }, 500);
      const totalGuests =
        (guests.adults || 0) + (guests.children || 0) + (guests.infants || 0);
      const maxGuests =
        listing.holidayPackage?.max_guests ??
        listing.holidayPackage?.defined_guests;
      if (maxGuests && totalGuests > maxGuests) {
        toast.error(
          `Questo pacchetto può ospitare al massimo ${maxGuests} ospiti`,
        );
        return;
      }
      // Il prezzo del pacchetto è prezzo × ospiti, indipendente dalla durata
      // (stessa formula del totale mostrato nel box prenotazione).
      const total_cleaning_fee = 0;
      const rawPrice =
        Number(listing.holidayPackage?.price) * (guestCount || 1) +
        total_cleaning_fee;

      const bookingData: BookingDataType = {
        id: `${listing.holidayPackage?.package_uuid}-${moment().format("YYYYMMDDHHmmss")}`,
        item: listing.holidayPackage,
        type: StockEnum.HOLIDAY_PACKAGE,
        startDate: moment(startDate).format("YYYY-MM-DD"),
        endDate: moment(endDate).format("YYYY-MM-DD"),
        guests: totalGuests,
        price: rawPrice,
        additional_fees: 0,
      };
      dispatch(setBooking(bookingData));
      setShowFooterSticky(false);
      toast.success("Prenotazione aggiunta al tuo viaggio");
    }
  };

  const handleFetchAvailability = async () => {
    try {
      const availability = await checkIfPropertyIsAvailable({
        property_uuid: listing.holidayPackage.package_uuid,
        check_in: moment(startDate).format("YYYY-MM-DD"),
        check_out: moment(endDate).format("YYYY-MM-DD"),
      }).unwrap();
    } catch (error) {}
  };

  const handleFetchAvailabilityInRangeByInitialDate = async () => {
    try {
      // const today = moment().format("YYYY-MM-DD");
      const month = moment().format("MM");
      const year = moment().format("YYYY");
      const nextMonth = moment().add(1, "month").format("MM");

      const response = await checkAvailabilityInRange({
        property_uuid: listing.holidayPackage.package_uuid,
        start_month: month,
        end_month: nextMonth,
        year: year,
      }).unwrap();

      dispatch(setAvailability(response));
    } catch (error) {}
  };

  const handleSave = (experience_uuid: string) => {
    toast.info("Funzionalità in arrivo");
  };

  const handleShare = (experience_uuid: string) => {
    toast.info("Funzionalità in arrivo");
  };

  // EVENT HANDLERS
  const onMonthChange = useCallback(
    async (date: Date) => {
      try {
        // get month and next month from date
        const month = moment(date).format("MM");
        const year = moment(date).format("YYYY");
        const nextMonth = moment(date).add(1, "month").format("MM");

        const response = await checkAvailabilityInRange({
          property_uuid: listing.holidayPackage.package_uuid,
          start_month: month,
          end_month: nextMonth,
          year: year,
        }).unwrap();

        dispatch(setAvailability(response));
      } catch (error) {}
    },
    [
      /* dipendenze */
    ],
  );

  function closeModalAmenities() {
    setIsOpenModalAmenities(false);
  }

  function openModalAmenities() {
    setIsOpenModalAmenities(true);
  }

  // EFFECTS
  useEffect(() => {
    handleFetchAvailabilityInRangeByInitialDate();
  }, [listing?.holidayPackage?.package_uuid]);

  useEffect(() => {
    //dispatch(setBookings([]));
  }, []);

  useEffect(() => {
    if (listing && isSuccess && !isLoading && !isSuccessExperiencesNearby) {
      handleFetchExperiences();
    }
    if (!isLoading && isSuccess) {
      // For holiday packages, we don't need to set property in Redux
      // dispatch(setProperty(listing.holidayPackage));
    }
  }, [isSuccess]);

  // Per i pacchetti con durata fissa il check-out non è modificabile:
  // è sempre check-in + durata (la durata conta come notti, quindi una
  // durata di 1 giorno dà check-out al giorno successivo).
  useEffect(() => {
    const duration = listing?.holidayPackage?.duration_days;
    if (duration) {
      setEndDate(moment(startDate).add(duration, "days").toDate());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing?.holidayPackage?.duration_days]);

  useEffect(() => {
    console.log("propertyIsAvailable", listing);
    if (listing && listing.holidayPackage) {
      document.title = listing.holidayPackage.name;
    }
  }, [listing]);

  // COMPONENTS
  const renderSectionExperiences = () => {
    if (isLoadingExperiences) {
      return Array(3)
        .fill(0)
        .map((_, index) => (
          <div key={index} className="w-full md:w-1/3 lg:w-1/4 p-2">
            <Skeleton className="w-full h-[20rem]" />
          </div>
        ));
    }

    const experiences = listing.holidayPackage?.linkedExperiences;

    if (!experiences || experiences.length === 0) {
      return (
        <div className="listingSection__wrap">
          <h2 className="text-xl sm:text-2xl font-semibold">
            Esperienze incluse nel pacchetto
          </h2>
          <div className="text-neutral-6000 dark:text-neutral-300 max-w-full">
            <p className="text-sm sm:text-base">
              Nessuna esperienza disponibile per questo pacchetto.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="listingSection__wrap">
        <h2 className="text-xl sm:text-2xl font-semibold">
          Esperienze incluse nel pacchetto
        </h2>
        <div className="text-neutral-6000 dark:text-neutral-300 max-w-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {experiences.map((experience: any, index: number) => (
              <FullImageCard
                key={index}
                image={experience.cover}
                title={experience.name}
                aspectRatio="square"
                className="w-full"
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderSectionGallery = () => {
    return (
      <div className="mt-[2rem] mb-[2rem] -mx-4 lg:-mx-0">
        <div className="w-full px-4 lg:px-0 space-y-8 lg:space-y-10">
          <h2 className="text-2xl font-semibold">Foto della struttura</h2>
          <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
          <div className="text-neutral-6000 dark:text-neutral-300">
            <span>
              Qui puoi vedere le foto della struttura e di tutte le sue stanze e
              aree comuni. Se necessiti di ulteriori informazioni, puoi sempre
              chiedere all'host delle foto aggiuntive
            </span>
          </div>
        </div>
        <Carousel>
          <div
            onClick={() => setIsImageFullScreen(listing.holidayPackage?.cover)}
            className="max-w-full max-h-[19rem]"
          >
            <img src={listing.holidayPackage?.cover_image} />
          </div>
          {listing.holidayPackage?.gallery_images?.map(
            (item: string, index: number) => (
              <div
                onClick={() => setIsImageFullScreen(item)}
                key={index}
                className="max-w-full max-h-[19rem]"
              >
                <img src={item} />
              </div>
            ),
          )}
        </Carousel>
        <Transition appear show={isImageFullScreen.length > 0} as={Fragment}>
          <Dialog
            as="div"
            className="fixed inset-0 z-50 overflow-y-auto"
            onClose={() => setIsImageFullScreen("")}
          >
            <div className="min-h-screen px-2 sm:px-4 text-center flex items-center justify-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-90" />
              </Transition.Child>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <div className="relative w-full h-full max-w-7xl max-h-[90vh] flex items-center justify-center p-4">
                  <button
                    onClick={() => setIsImageFullScreen("")}
                    className="absolute top-4 right-4 z-10 p-2 bg-black bg-opacity-50 rounded-full hover:bg-opacity-70 transition-all"
                  >
                    <XCircleIcon className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                  </button>
                  <img
                    src={isImageFullScreen}
                    className="max-w-full max-h-full object-contain rounded-lg"
                    alt="Full screen image"
                  />
                </div>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition>
      </div>
    );
  };

  const getIconFromAmenities = (name: string) => {
    // Use the comprehensive AMENITIES_ICONS from constants
    const icon = AMENITIES_ICONS.find(
      (item) => item.name.toLowerCase() === name.toLowerCase(),
    );
    if (icon) {
      return icon.icon;
    }

    // Fallback to basic icon mapping for common amenities
    const fallbackIcons = [
      { name: "wifi", icon: "la-wifi" },
      { name: "tv", icon: "la-tv" },
      { name: "aria condizionata", icon: "la-fan" },
      { name: "asciugatore", icon: "la-fan" },
      { name: "ventilatore", icon: "la-fan" },
      { name: "asciugacapelli", icon: "la-fan" },
      { name: "lavastoviglie", icon: "la-fan" },
      { name: "prodotti per la cura personale", icon: "la-bath" },
      { name: "scrivania", icon: "la-desktop" },
      { name: "cassaforte", icon: "la-lock" },
      { name: "armadio", icon: "la-tshirt" },
    ];
    const fallbackIcon = fallbackIcons.find(
      (item) => item.name.toLowerCase() === name.toLowerCase(),
    );
    return fallbackIcon?.icon || "la-check";
  };

  const renderSection3 = () => {
    // Get amenities data - try multiple sources
    let amenitiesData: string[] = [];

    if (
      Array.isArray(Object.values(listing.linkedProperties)) &&
      Object.values(listing.linkedProperties).length > 0
    ) {
      // Extract amenities from all properties and remove duplicates
      const amenitiesSet = new Set<string>();
      Object.values(listing.linkedProperties).forEach((property: any) => {
        if (Array.isArray(property.amenities)) {
          property.amenities.forEach((amenity: string) => {
            amenitiesSet.add(amenity);
          });
        }
      });
      amenitiesData = Array.from(amenitiesSet);
    } else {
      amenitiesData = [];
    }

    if (!amenitiesData || amenitiesData.length === 0) {
      return (
        <div className="listingSection__wrap">
          <h2 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">
            Servizi e comfort
          </h2>
          <div className="w-14 border-b border-neutral-200 dark:border-neutral-700 mb-4 sm:mb-6"></div>
          <div className="text-neutral-6000 dark:text-neutral-300">
            <p className="text-sm sm:text-base">
              Nessun servizio disponibile per questa struttura.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="listingSection__wrap">
        <h2 className="text-xl sm:text-2xl font-semibold">Servizi e comfort</h2>
        <div className="flex flex-wrap sm:flex lg:grid-cols-6 gap-3 sm:gap-4 text-sm text-neutral-700 dark:text-neutral-300">
          {amenitiesData
            .filter((_: any, i: number) => i < 10)
            .map((item: string) => (
              <AmenitiesTag
                key={item}
                name={item}
                variant="compact"
                size="sm"
              />
            ))}
          {/* ----- */}
          {amenitiesData.length > 10 && (
            <div className="col-span-1 sm:col-span-2 md:col-span-3 lg:col-span-6">
              <ButtonSecondary
                onClick={openModalAmenities}
                className="w-full sm:w-auto"
              >
                Vedi altri {amenitiesData.length - 10} servizi
              </ButtonSecondary>
            </div>
          )}
          {renderModalAmenities()}
        </div>
      </div>
    );
  };

  const renderModalAmenities = () => {
    // Get amenities data - try multiple sources
    let amenitiesData: string[] = [];

    if (
      Array.isArray(Object.values(listing.linkedProperties)) &&
      Object.values(listing.linkedProperties).length > 0
    ) {
      // Extract amenities from all properties and remove duplicates
      const amenitiesSet = new Set<string>();
      Object.values(listing.linkedProperties).forEach((property: any) => {
        if (Array.isArray(property.amenities)) {
          property.amenities.forEach((amenity: string) => {
            amenitiesSet.add(amenity);
          });
        }
      });
      amenitiesData = Array.from(amenitiesSet);
    } else {
      amenitiesData = [];
    }

    return (
      <Transition appear show={isOpenModalAmenities} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          onClose={closeModalAmenities}
        >
          <div className="min-h-screen px-2 sm:px-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block py-4 sm:py-8 my-4 h-auto w-full max-w-4xl max-h-[90vh]">
                <div className="inline-flex pb-2 flex-col w-full text-left align-middle transition-all transform overflow-hidden rounded-xl sm:rounded-2xl bg-white dark:bg-neutral-900 dark:border dark:border-neutral-700 dark:text-neutral-100 shadow-xl h-full">
                  <div className="relative flex-shrink-0 px-4 sm:px-6 py-3 sm:py-4 border-b border-neutral-200 dark:border-neutral-800">
                    <h3 className="text-base sm:text-lg font-medium leading-6 text-gray-900 dark:text-white text-center pr-8">
                      Servizi e comfort
                    </h3>
                    <span className="absolute left-2 sm:left-3 top-2 sm:top-3">
                      <ButtonClose onClick={closeModalAmenities} />
                    </span>
                  </div>
                  <div className="px-4 sm:px-6 overflow-auto text-neutral-700 dark:text-neutral-300 divide-y divide-neutral-200 dark:divide-neutral-700 z-[90] relative max-h-[60vh] sm:max-h-[70vh]">
                    {amenitiesData.length > 0 ? (
                      <div className="flex flex-col sm:grid sm:grid-cols-2 gap-3 sm:gap-4 p-2 sm:p-4">
                        {amenitiesData.map((item) => (
                          <div
                            key={item}
                            className="flex items-start sm:items-center p-3 sm:p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors text-left"
                          >
                            <AmenitiesTag
                              name={item}
                              variant="modal"
                              size="md"
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-neutral-500 dark:text-neutral-400">
                          Nessun servizio disponibile
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    );
  };

  const renderSection6 = () => {
    return (
      <div className="listingSection__wrap">
        {/* HEADING */}
        <h2 className="text-2xl font-semibold">
          Recensioni ({reviews?.data?.length * reviews?.totalPages} recensioni)
        </h2>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>

        {/* comment */}
        <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
          {reviews?.data?.map((item: any, index: number) => (
            <CommentListing key={index} data={item} />
          ))}
          <div className="pt-8 w-full flex items-center justify-center">
            <Pagination
              totalPages={reviews?.totalPages || 0}
              currentPage={reviews?.currentPage || 0}
              onPageChange={(page) => fetchReviews({ property_uuid: packageUuid, page })}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderMap = () => {
    return (
      <div className="listingSection__wrap">
        <div className="w-full md:w-3/4">
          {/* HEADING */}
          <div>
            <h2 className="text-2xl font-semibold">Location</h2>
            <span className="block mt-2 text-neutral-500 dark:text-neutral-400">
              {listing.linkedProperties?.[0]?.address}{" "}
              {listing.linkedProperties?.[0]?.building} ,
              {listing.linkedProperties?.[0]?.city},{" "}
              {listing.linkedProperties?.[0]?.country}
            </span>
          </div>
          <div className="w-14 border-b border-neutral-200 dark:border-neutral-700 mt-4" />

          {/* MAP */}
          <div className="aspect-w-5 mt-4 md:mt-4 aspect-h-5 sm:aspect-h-3 ring-1 ring-black/10 rounded-xl z-0">
            <div className="rounded-xl overflow-hidden z-0">
              {listing.holidayPackage?.latitude &&
                listing.holidayPackage?.longitude && (
                  <LocationsMap
                    defaultCenter={{
                      lat: Number(listing?.holidayPackage?.latitude),
                      lng: Number(listing?.holidayPackage?.longitude),
                    }}
                    markers={[
                      {
                        lat: Number(listing?.holidayPackage?.latitude),
                        lng: Number(listing?.holidayPackage?.longitude),
                      },
                    ]}
                  />
                )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSection8 = () => {
    return (
      <div className="listingSection__wrap ">
        <div className="w-full space-y-2 md:w-3/4">
          {/* HEADING */}
          <h2 className='text-2xl font-semibold'>Politiche e condizioni</h2>
          <div className='w-14 border-b border-neutral-200 dark:border-neutral-700' />

          {/* CONTENT */}
          <div>
            <h4 className="text-lg font-semibold">Check-in e check-out</h4>
            <div className="mt-3 text-neutral-500 dark:text-neutral-400 max-w-md text-sm sm:text-base">
              <div className="flex space-x-10 justify-between p-3 bg-neutral-100 dark:bg-neutral-800 rounded-lg">
                <span>Check-in</span>
                <span>{listing?.linkedProperties?.[0]?.check_in}</span>
              </div>
              <div className="flex space-x-10 justify-between p-3">
                <span>Check-out</span>
                <span>{listing?.linkedProperties?.[0]?.check_out} </span>
              </div>
            </div>
          </div>

          {/* <div className='w-14 border-b border-neutral-200 dark:border-neutral-700' /> */}

          {/* CONTENT */}
          <div>
            <h4 className='text-lg font-semibold'>Cancellazione</h4>
            <span className='block mt-3 text-neutral-500 dark:text-neutral-400'>
              <Link to={"/terms-and-conditions"}>{t("detailPages.common.visitTermsAndConditions") || "Visita la pagina di termini e condizioni per maggiori informazioni."}</Link>
            </span>
          </div>

          {/* <div className='w-14 border-b border-neutral-200 dark:border-neutral-700' /> */}

          {/* CONTENT */}
          {/* <div>
            <h4 className='text-lg font-semibold'>Note speciali</h4>
            <div className='prose sm:prose'>
              <ul className='mt-3 text-neutral-500 dark:text-neutral-400 space-y-2'>
                <li>
                  Ban and I will work together to keep the landscape and
                  environment green and clean by not littering, not using
                  stimulants and respecting people around.
                </li>
                <li>Do not sing karaoke past 11:30</li>
              </ul>
            </div>
          </div>
          <div>
            <h4 className='text-lg font-semibold'>Cusr</h4>
            <div className='prose sm:prose'>
              {listing?.linkedProperties?.[0]?.cusr}
            </div>
          </div> */}
        </div>
      </div>
    );
  };

  const renderStatsSection = () => {
    const worksData = {
      labels: propertyStats?.works?.labels,
      datasets: [
        {
          data: propertyStats?.works?.counts,
          backgroundColor: [
            "rgba(247, 60, 105,0.9)",
            "rgba(54, 162, 235,0.9)",
            "rgba(255, 206, 86,0.9)",
            "rgba(75, 192, 192,0.9)",
            "rgba(153, 102, 255,0.9)",
            "rgba(255, 159, 64,0.9)",
          ],
          borderColor: [
            "rgba(255, 99, 132, 1)",
            "rgba(54, 162, 235, 1)",
            "rgba(255, 206, 86, 1)",
            "rgba(75, 192, 192, 1)",
            "rgba(153, 102, 255, 1)",
            "rgba(255, 159, 64, 1)",
          ],
          borderWidth: 1,
        },
      ],
    };
    const agesData = {
      labels: propertyStats?.ages?.labels,
      datasets: [
        {
          label: "Anni di nascita",
          data: propertyStats?.ages?.counts,
          backgroundColor: [
            "rgba(247, 60, 105,0.9)",
            "rgba(54, 162, 235,0.9)",
            "rgba(255, 206, 86,0.9)",
            "rgba(75, 192, 192,0.9)",
            "rgba(153, 102, 255,0.9)",
            "rgba(255, 159, 64,0.9)",
          ],
          borderColor: [
            "rgba(255, 99, 132, 1)",
            "rgba(54, 162, 235, 1)",
            "rgba(255, 206, 86, 1)",
            "rgba(75, 192, 192, 1)",
            "rgba(153, 102, 255, 1)",
            "rgba(255, 159, 64, 1)",
          ],
          borderWidth: 1,
        },
      ],
    };
    const citiesData = {
      labels: propertyStats?.cities?.labels,
      datasets: [
        {
          data: propertyStats?.cities?.counts,
          backgroundColor: [
            "rgba(247, 60, 105,0.9)",
            "rgba(54, 162, 235,0.9)",
            "rgba(255, 206, 86,0.9)",
            "rgba(75, 192, 192,0.9)",
            "rgba(153, 102, 255,0.9)",
            "rgba(255, 159, 64,0.9)",
          ],
          borderColor: [
            "rgba(255, 99, 132, 1)",
            "rgba(54, 162, 235, 1)",
            "rgba(255, 206, 86, 1)",
            "rgba(75, 192, 192, 1)",
            "rgba(153, 102, 255, 1)",
            "rgba(255, 159, 64, 1)",
          ],
          borderWidth: 1,
        },
      ],
    };
    if (isLoadingStats) {
      return (
        <div className="w-full md:w-1/3 lg:w-1/4 p-2">
          <Skeleton className="w-full h-[20rem]" />
        </div>
      );
    }
    if (
      propertyStats.works.labels.length &&
      propertyStats.ages.labels.length &&
      propertyStats.cities.labels.length
    ) {
      return (
        <div className="listingSection__wrap">
          <h2 className="text-xl sm:text-2xl font-semibold">La tribù:</h2>
          <p className="text-neutral-500 dark:text-neutral-400 !mt-2 text-sm sm:text-base">
            Scopri chi sono i viaggiatori che hanno scelto questo posto e quali
            sono le loro caratteristiche
          </p>
          <div className="flex flex-col lg:flex-row lg:items-start gap-8 lg:gap-0 !mt-6 sm:!mt-8 lg:!mt-10">
            <div className="flex flex-col w-full lg:w-auto lg:basis-[30%] items-center lg:items-start">
              <div className="w-full max-w-[13rem] mb-4">
                <Badge
                  className="!text-[0.8rem] !p-2"
                  name="Settore lavorativo"
                  color="red"
                />
              </div>
              <div className="w-full max-w-[200px] h-[200px]">
                <Doughnut
                  data={worksData}
                  options={{ maintainAspectRatio: false }}
                />
              </div>
            </div>
            {/* separator */}
            <div className="hidden lg:block lg:w-[1px] bg-gray-300 lg:mx-[1rem]"></div>
            {/* middle */}
            <div className="flex flex-col w-full lg:w-auto lg:basis-[40%] items-center lg:items-start mt-6 lg:mt-0">
              <div className="w-full max-w-[13rem] mb-4">
                <Badge
                  className="!text-[0.8rem] !p-2"
                  name="Età media"
                  color="green"
                />
              </div>
              <div className="w-full max-w-[300px] h-[200px]">
                <Bar data={agesData} options={{ maintainAspectRatio: false }} />
              </div>
            </div>
            {/* separator */}
            <div className="hidden lg:block lg:w-[1px] bg-gray-300 lg:mx-[1rem]"></div>
            {/* right */}
            <div className="flex flex-col w-full lg:w-auto lg:basis-[30%] items-center lg:items-start mt-6 lg:mt-0">
              <div className="w-full max-w-[13rem] mb-4">
                <Badge
                  className="!text-[0.8rem] !p-2"
                  name="Provenienza"
                  color="blue"
                />
              </div>
              <div className="w-full max-w-[200px] h-[200px]">
                <Doughnut
                  data={citiesData}
                  options={{ maintainAspectRatio: false }}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }
  };

  const renderSection2 = () => {
    return (
      <div className="listingSection__wrap">
        <h2 className="text-2xl font-semibold">Descrizione</h2>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
        <div className="text-neutral-6000 dark:text-neutral-300 max-w-full">
          <span className="max-w-full break-words">
            {listing.holidayPackage?.description}
          </span>
        </div>
      </div>
    );
  };

  const hostDetailsSection = () => {
    return (
      <div className="listingSection__wrap">
        {/* HEADING */}
        <div className="flex flex-row items-center justify-between">
          <h2 className="text-2xl font-semibold">{t("translation:detailPages.common.theHost")}</h2>
          <ButtonSecondary href={`/author/${listing?.holidayPackage?.host_uuid}`}>{t("translation:detailPages.common.goToHostProfile")}</ButtonSecondary>
        </div>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>

        {/* host */}
        <div className="flex items-center space-x-4">
          <Avatar
            hasChecked
            hasCheckedClass="w-4 h-4 -top-0.5 right-0.5"
            sizeClass="h-14 w-14"
            radius="rounded-full"
            imgUrl={listing?.host?.avatar || ""}
          />
          <div>
            <Link className="block text-xl font-medium break-words" to={`/author/${listing?.holidayPackage?.host_uuid}`}>
              {listing?.host?.name || ""}
            </Link>
            <div className="mt-1.5 flex items-center text-sm text-neutral-500 dark:text-neutral-400">
              <StartRating
                point={listing?.host?.rating || 0}
                reviewCount={listing?.host?.total_reviews || 0}
              />
              <span className="mx-2">·</span>
              <span> 12 places</span>
            </div>
          </div>
        </div>

        {/* desc */}
        <span className="block text-neutral-6000 dark:text-neutral-300 break-words">
          {listing?.host?.description || ""}
        </span>

        {/* info */}
        <div className="block text-neutral-500 dark:text-neutral-400 space-y-2.5 break-words">
          {listing?.host?.joined_date && (
            <div className="flex items-center space-x-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span>
                {t("translation:detailPages.common.joinedIn", {
                  date: listing?.host?.joined_date || "",
                })}
              </span>
            </div>
          )}
          {listing?.host?.response_percentage && (
            <div className="flex items-center space-x-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                />
              </svg>
              <span>
                {t("translation:detailPages.common.hostResonseRate", {
                  percentage: listing?.host?.response_rate || "",
                })}
              </span>
            </div>
          )}
          {listing?.host?.response_time && (
            <div className="flex items-center space-x-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>
                {t("translation:detailPages.common.hostResonseTime", {
                  time: listing?.host?.response_time || "",
                })}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const bookingActionSidebar = () => {
    return (
      <div className="listingSectionSidebar__wrap shadow-xl p-4 sm:p-6">
        {/* PRICE */}
        <div className="flex justify-between items-start sm:items-center mb-4 sm:mb-6">
          <div className="flex flex-col">
            <span className="text-2xl sm:text-3xl font-semibold leading-tight">
              {formatPrice(
                listing?.holidayPackage?.price || 0,
                true,
                window.navigator.language,
                listing?.holidayPackage?.currency || "EUR",
              )}
            </span>
            <span className="text-sm sm:text-base font-normal text-neutral-500 dark:text-neutral-400">
              /person
            </span>
          </div>
          <div className="mt-1 sm:mt-0">
            <StartRating />
          </div>
        </div>

        {/* FORM */}
        <form className="flex flex-col border border-neutral-200 dark:border-neutral-700 rounded-2xl sm:rounded-3xl mb-4 sm:mb-6">
          <StayDatesRangeInputWrapper
            className="flex-1 z-[11]"
            startDateParam={startDate}
            endDateParam={endDate}
            onChange={onChangeDate}
            onMonthChange={onMonthChange}
            rangeAvailability={rangeAvailability}
          />
          <div className="w-full border-b border-neutral-200 dark:border-neutral-700"></div>
          {/* Guest input disable as for Figma comment */}
          <div className="flex-1 opacity-50 pointer-events-none">
            <GuestsInput />
          </div>
        </form>

        {/* SUM */}
        <div className="flex flex-col space-y-3 sm:space-y-4 mb-4 sm:mb-6">
          <div className="flex justify-between text-neutral-6000 dark:text-neutral-300 text-sm sm:text-base">
            <span>{`${formatPrice(
              listing?.holidayPackage?.price || 0,
              true,
              window.navigator.language,
              listing?.holidayPackage?.currency || "EUR",
            )} x ${guestCount || 1}`}</span>
            <span>{totalFormattedPrice}</span>
          </div>
          <div className="border-b border-neutral-200 dark:border-neutral-700"></div>
          <div className="flex justify-between font-semibold text-base sm:text-lg">
            <span>Total</span>
            <span>{totalFormattedPrice}</span>
          </div>
        </div>

        {/* SUBMIT */}
        {isWithinLatestBooking(
          startDate,
          listing?.holidayPackage?.latest_booking
        ) ? (
          <ButtonPrimary
            onClick={() =>
              !showDatePicker
                ? handleRequestBooking()
                : toast.error("Seleziona le date per continuare")
            }
            className="w-full py-3 sm:py-4 text-sm sm:text-base min-h-[44px] touch-manipulation"
            disabled={listing?.holidayPackage?.stopped}
            loading={isSendingRequest}
          >
            {t("translation:detailPages.common.sendRequest")}
          </ButtonPrimary>
        ) : (
          <ButtonPrimary
            onClick={() =>
              !showDatePicker
                ? handleStartBooking(false)
                : toast.error("Seleziona le date per continuare")
            }
            className="w-full py-3 sm:py-4 text-sm sm:text-base min-h-[44px] touch-manipulation"
            disabled={listing?.holidayPackage?.stopped}
            loading={isCheckingPropertyAvailability || loading}
          >
            {t("translation:detailPages.common.addToTrip")}
          </ButtonPrimary>
        )}
      </div>
    );
  };

  const primaryInfoSection = () => {
    return (
      <div className="listingSection__wrap !space-y-6">
        {/* 1 */}
        <div className="flex justify-between items-center">
          <Badge
            color={returnMoodColor(listing?.holidayPackage?.mood as MoodEnum)}
            name={listing?.holidayPackage?.mood || ""}
          />
          <LikeSaveBtns
            actionSave={() => handleSave(listing?.holidayPackage?.package_uuid)}
            actionShare={() =>
              handleShare(listing?.holidayPackage?.package_uuid)
            }
          />
        </div>

        {/* 2 */}
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold">
          {listing?.holidayPackage?.name || "No title"}
        </h2>

        {/* 3 */}
        <div className="flex items-center space-x-4">
          <StartRating
            point={listing?.holidayPackage?.rating || 0}
            reviewCount={listing?.holidayPackage?.total_reviews || 0}
          />
          <span>
            <i className="las la-map-marker-alt"></i>
            <span className="ml-1">
              {listing?.holidayPackage?.address || "No address"}
            </span>
          </span>
        </div>

        {/* 4 */}
        <div className="flex items-center">
          <Avatar
            hasChecked
            sizeClass="h-10 w-10"
            radius="rounded-full"
            imgUrl={listing?.host?.avatar || ""}
          />
          <span className="ml-2.5 text-neutral-500 dark:text-neutral-400">
            {`${t("translation:detailPages.common.hostedBy")} `}
            <span className="text-neutral-900 dark:text-neutral-200 font-medium">
              {listing?.host?.name || "Host Unknown"}
            </span>
          </span>
        </div>

        {/* 5 */}
        <div className="w-full border-b border-neutral-100 dark:border-neutral-700" />

        {/* 6 */}
        <div className="flex items-center justify-between xl:justify-start space-x-8 xl:space-x-12 text-sm text-neutral-700 dark:text-neutral-300">
          <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 text-center sm:text-left sm:space-x-3 ">
            <i className="las la-clock text-2xl"></i>
            <span className="">{`${listing?.holidayPackage?.duration_days || 0} ${t("translation:detailPages.holidayPackage.durationUnit")}`}</span>
          </div>
          <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 text-center sm:text-left sm:space-x-3 ">
            <i className="las la-user-friends text-2xl"></i>
            <span className="">
              {/* Here can be done also by checking package type */}
              {listing?.holidayPackage?.defined_guests !== undefined &&
              listing?.holidayPackage?.defined_guests !== null
                ? t("translation:detailPages.holidayPackage.guestsUpTo", {
                    count: listing.holidayPackage.defined_guests,
                  })
                : t("translation:detailPages.holidayPackage.guestsFromTo", {
                    min: listing?.holidayPackage?.min_guests || 1,
                    max: listing?.holidayPackage?.max_guests || 10,
                  })}
            </span>
          </div>
          {/* <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 text-center sm:text-left sm:space-x-3 ">
            <i className="las la-language text-2xl"></i>
            <span className="">English, VietNames</span>
          </div> */}
        </div>
      </div>
    );
  };

  const onChangeDate = (dates: [Date | null, Date | null]) => {
    const [start] = dates;

    // Skip if start date is null
    if (!start) return;

    // La durata del pacchetto comanda: il check-out = check-in + durata
    // (la durata conta come notti). Fallback su range solo se il pacchetto
    // non ha una durata impostata.
    const duration = listing?.holidayPackage?.duration_days;
    const calculatedEndDate = duration
      ? moment(start).add(duration, "days").toDate()
      : moment(start).add(range - 1, "days").toDate();

    const startMonth = moment(start).format("MM");
    const endMonth = moment(calculatedEndDate).format("MM");

    const startDay = moment(start).format("DD");
    const endDay = moment(calculatedEndDate).format("DD");

    let isDateBooked = false;

    // Controllo se una data selezionata è già prenotata
    if (rangeAvailability) {
      for (
        let month = parseInt(startMonth, 10);
        month <= parseInt(endMonth, 10);
        month++
      ) {
        const monthStr = month < 10 ? `0${month}` : `${month}`;
        const daysInMonth = rangeAvailability[monthStr];
        if (daysInMonth) {
          for (
            let day = parseInt(startDay, 10);
            day <= parseInt(endDay, 10);
            day++
          ) {
            const dayStr = day < 10 ? `0${day}` : `${day}`;
            if (daysInMonth.includes(dayStr)) {
              isDateBooked = true;
              break;
            }
          }
        }
        if (isDateBooked) break;
      }
    }

    if (isDateBooked) {
      toast.error("Non puoi selezionare queste date perchè sono già prenotate");
    } else {
      setStartDate(start as Date);
      setEndDate(calculatedEndDate);
    }
  };

  const locationSection = () => {
    const properties = listing?.linkedProperties
      ? (Object.values(listing.linkedProperties) as any)
      : [];

    const markers = properties
      .filter((p: any) => p?.latitude && p?.longitude)
      .map((prop: any) => ({
        lat: Number(prop.latitude),
        lng: Number(prop.longitude),
      }));

    // Calculate center point from all markers
    const calculateCenter = () => {
      if (markers.length === 0) {
        return { lat: 0, lng: 0 };
      }

      const avgLat =
        markers.reduce((sum, m) => sum + m.lat, 0) / markers.length;
      const avgLng =
        markers.reduce((sum, m) => sum + m.lng, 0) / markers.length;

      return { lat: avgLat, lng: avgLng };
    };

    const centerPoint = calculateCenter();

    return (
      <div className="listingSection__wrap">
        {/* HEADING */}
        <div>
          <h2 className="text-2xl font-semibold">
            {t("translation:detailPages.common.location")}
          </h2>
          {/* <span className="block mt-2 text-neutral-500 dark:text-neutral-400 break-words">
            {properties[0]?.address || ""} {properties[0]?.building || ""}, {properties[0]?.city || ""}, {properties[0]?.country || ""}
          </span> */}
        </div>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700" />

        {/* MAP */}
        <div className="aspect-w-5 aspect-h-5 sm:aspect-h-3 ring-1 ring-black/10 rounded-xl z-0">
          <div className="rounded-xl overflow-hidden z-0">
            <LocationsMap
              className="rounded-xl"
              defaultCenter={centerPoint}
              markers={markers}
            />
          </div>
        </div>
      </div>
    );
  };

  if (isLoading || !listing) {
    return (
      <main className="w-full overflow-hidden">
        <div className="w-full h-96">
          <Skeleton height={"100%"} width={"100%"} />
        </div>
        <div className="mt-5 pl-5">
          <Skeleton height={50} width={"80%"} />
        </div>
        <div className="mt-5 pl-5">
          <Skeleton height={200} width={"80%"} />
        </div>
      </main>
    );
  }

  return (
    <>
      {/* SINGLE HEADER || Gallery */}
      <ImageMosaicGallery
        coverImage={listing?.holidayPackage?.cover_image || ""}
        galleryImages={listing?.holidayPackage?.gallery_images || []}
        onOpenGallery={handleOpenModalImageGallery}
        alt="Holiday Package"
      />

      <main className="relative z-10 my-6 sm:my-8 lg:my-11 flex flex-col lg:flex-row">
        <div className="flex flex-col px-3 sm:px-4 lg:flex-row max-w-4xl lg:max-w-5xl xl:max-w-6xl mx-auto w-full">
          {/* CONTENT */}
          <div className="flex flex-col w-full space-y-6 sm:space-y-8 lg:w-3/5 xl:w-2/3 lg:pr-6 xl:pr-10 lg:space-y-8 xl:space-y-10">
            {primaryInfoSection()}
            <div className="block lg:hidden mb-8">{bookingActionSidebar()}</div>

            {renderSection2()}

            <SectionStayDays
              linkedProperties={Object.values(listing?.linkedProperties)} // pass list instead of object
              linkedExperiences={Object.values(listing?.linkedExperiences)} // pass list instead of object
              package_content={listing?.holidayPackage?.package_content}
              duration_days={listing?.holidayPackage?.duration_days}
              isLoading={isLoading}
            />

            {renderSectionExperiences()}

            {renderSection3()}
            {locationSection()}
            {renderStatsSection()}
            {renderSection8()}
          </div>

          {/* SIDEBAR */}
          <div className="hidden lg:block lg:w-2/5 xl:w-1/3 mt-14 lg:mt-0">
            <div className="sticky top-28 space-y-8">
              {bookingActionSidebar()}
              {hostDetailsSection()}
              {renderSection6()}
            </div>
          </div>
        </div>
      </main>
      <ModalLogin
        showModal={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={() => handleStartBooking(true)}
      />
      <Transition show={showFooterSticky} as={Fragment}>
        <Transition.Child>
          <MobileFooterSticky
            isAvailable={true}
            price={listing.holidayPackage.price}
            rating={listing.holidayPackage.rating}
            reviews={listing.holidayPackage.reviews}
            showDateModal
            startDate={startDate}
            endDate={endDate}
            handleStartBooking={() => handleStartBooking(false)}
            onChangeDate={onChangeDate}
            rangeAvailability={rangeAvailability}
          />
        </Transition.Child>
      </Transition>
    </>
  );
};

export default function HolidayPackageDetailPage() {
  return (
    <DetailPagetLayout>
      <HolidayPackagePageContainer />
    </DetailPagetLayout>
  );
}
